import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { VehicleCheckActions } from './vehicle-check.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FleetService } from '@core/services/fleet.service';
import { HelperService } from '@core/helper/helper.service';

@Injectable()
export class VehicleCheckEffects {
  private actions$ = inject(Actions);
  private fleetService = inject(FleetService);
  private translateService = inject(TranslateService);
  helperService = inject(HelperService);
  getInspectedVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleCheckActions.loadInspectedVehicles),
      mergeMap((data) =>
        this.fleetService.getVehicleInspectedList(data).pipe(
          map((companies) =>
            companies.success
              ? VehicleCheckActions.loadInspectedVehiclesSuccess(companies)
              : VehicleCheckActions.loadInspectedVehiclesFailure(companies)
          ),
          catchError((error) =>
            of(
              VehicleCheckActions.loadInspectedVehiclesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleCheckActions.exportPDF),
      mergeMap((payload) =>
        this.fleetService
          .getVehicleInspectedList({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
          })
          .pipe(
            map((data) =>
              data.success
                ? VehicleCheckActions.exportPDFSuccess({
                    ...data,
                    ...payload,
                  })
                : VehicleCheckActions.exportPDFFailure(data)
            ),
            catchError((error) =>
              of(
                VehicleCheckActions.exportPDFFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VehicleCheckActions.exportPDFSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.inspectionPDF(
              data.data,
              data.user?.label ?? 'All',
              data.start_date,
              data.end_date
            );
        })
      ),
    { dispatch: false }
  );

  markAsVerified$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleCheckActions.markAsVerified),
      mergeMap((data) =>
        this.fleetService.markAsVerified(data).pipe(
          map((response) => {
            if (response.success)
              return VehicleCheckActions.markAsVerifiedSuccess(response);
            else return VehicleCheckActions.markAsVerifiedFailure(response);
          }),
          catchError((error) =>
            of(
              VehicleCheckActions.markAsVerifiedFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  updateCorrectiveAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleCheckActions.updateCorrectiveAction),
      mergeMap(({ data }) =>
        this.fleetService.updateCorrectiveAction(data).pipe(
          map((response) => {
            if (response.success) {
              return VehicleCheckActions.updateCorrectiveActionSuccess(
                response
              );
            } else
              return VehicleCheckActions.updateCorrectiveActionFailure(
                response
              );
          }),
          catchError((error) =>
            of(
              VehicleCheckActions.updateCorrectiveActionFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );
}
